import { baseService } from "src/store/base.store";
import { CreditVoucher } from "./types/credit-voucher.entity";
import { CreateCreditVouchers, FilterCreditVouchers, RedeemVoucherRequest, RedeemVoucherResponse } from "./types";
import { creditVoucherRepository } from "./credit-voucher.repository";
import { executeQuery } from "src/store";
import { callFunctionSafe } from "src/firebase/functions";
import { cleanObject } from "src/utils/util";
import { convertLocalDateToJsDate } from "src/utils/date.util";
import { functions } from "src/firebase";
import { httpsCallable } from "firebase/functions";

const creditVoucherService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getCreditVouchers: builder.query<CreditVoucher[], { where: FilterCreditVouchers; offset?: string }>({
      queryFn: async ({ where, offset }) => await executeQuery(() => creditVoucherRepository.find({ where, offset })),
    }),

    getVoucherCodeById: builder.query<CreditVoucher, string>({
      queryFn: async (id) => await executeQuery(() => creditVoucherRepository.findById(id)),
    }),

    createCreditVoucher: builder.mutation<{ voucherCodes: string[] }, CreateCreditVouchers>({
      queryFn: async (creditVoucherData) => {
        const cleanedData = cleanObject({
          ...creditVoucherData,
          expiryDate: convertLocalDateToJsDate(creditVoucherData.expiryDate),
          subscriptionEndDate: creditVoucherData.subscriptionEndDate ? convertLocalDateToJsDate(creditVoucherData.subscriptionEndDate) : undefined,
        });
        return await callFunctionSafe<{ voucherCodes: string[] }>("createCreditVoucher", cleanedData);
      },
    }),

    countCreditVouchers: builder.query<number, { where: FilterCreditVouchers }>({
      queryFn: async ({ where }) => await executeQuery(() => creditVoucherRepository.count(where)),
    }),

    redeemVoucher: builder.mutation<RedeemVoucherResponse, RedeemVoucherRequest>({
      queryFn: async (redeemVoucherData) => {
        try {
          // Call Firebase function directly to preserve full error structure
          const callable = httpsCallable(functions, "redeemVoucher");
          const result = await callable(redeemVoucherData);
          return { data: result.data as RedeemVoucherResponse };
        } catch (error: any) {
          // Preserve the full Firebase error structure (code, message, details)
          // RTK Query expects { error: { status, data } } format
          const errorData: any = {
            message: error?.message || "An error occurred",
          };
          
          // Preserve error code if available
          if (error?.code) {
            errorData.code = error.code;
          }
          
          // Preserve error details (including i18nKey) if available
          if (error?.details) {
            errorData.details = error.details;
          }
          
          return { 
            error: { 
              status: error?.code === "permission-denied" ? 403 : 
                      error?.code === "not-found" ? 404 :
                      error?.code === "invalid-argument" ? 400 :
                      error?.code === "failed-precondition" ? 412 :
                      error?.code === "internal" ? 500 : 400, 
              data: errorData
            } 
          };
        }
      },
    }),
  }),
});

export const {
  useCreateCreditVoucherMutation,
  useGetCreditVouchersQuery,
  useGetVoucherCodeByIdQuery,
  useCountCreditVouchersQuery,
  useRedeemVoucherMutation,
} = creditVoucherService;
